import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col,
    Form, FormGroup, Label, Input, InputGroup, FormFeedback
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import Swal from 'sweetalert2';
import { Edit, Trash2 } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";
import { useForm, Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import { useConfig } from "../../Context/ConfigContext";
import "react-datepicker/dist/react-datepicker.css";

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface ExpenseData {
    id: number;
    title: string;
    description: string | null;
    amount: number;
    date: string;
    created_at: string;
}

interface ExpenseFormData {
    title: string;
    description: string;
    amount: string;
    date: string;
}

const displayDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};

export default function ExpenseManagement() {
    const [data, setData] = useState<ExpenseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const { config } = useConfig();
    const { hasPermission } = useAuth();

    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // react-hook-form
    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors }
    } = useForm<ExpenseFormData>();

    const canAdd = hasPermission('expense-management.add');
    const canEdit = hasPermission('expense-management.edit');
    const canDelete = hasPermission('expense-management.delete');

    const showForm = canAdd || (isEditing && canEdit);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, refresh, currentPage, perPage, sortBy, sortDir]);

    useEffect(() => {
        if (!isEditing) {
            reset({
                title: "",
                description: "",
                amount: "",
                date: ""
            });
        }
    }, [isEditing, reset]);

    const fetchData = async (page: number, size: number, sort: string, direction: string, search: string) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`expense/get_all?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search
            });
            setData(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            toast.error("Failed to load expenses");
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (formData: ExpenseFormData) => {
        setSubmitting(true);
        try {
            const endpoint = isEditing ? 'expense/update' : 'expense/store';
            const payload = isEditing ? { ...formData, id: editId } : formData;

            await apiPost(endpoint, payload);
            toast.success(isEditing ? "Expense updated successfully" : "Expense added successfully");

            setIsEditing(false);
            setEditId(null);
            setRefresh(prev => prev + 1);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (row: ExpenseData) => {
        setIsEditing(true);
        setEditId(row.id);
        reset({
            title: row.title,
            description: row.description || "",
            amount: row.amount.toString(),
            date: row.date
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`expense/delete`, { id });
                    toast.success("Expense deleted successfully");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete expense");
                }
            }
        });
    };

    const columns: any[] = [
        {
            name: <span className="fw-bold">Sl.No</span>,
            selector: (row: ExpenseData, index: number) => (currentPage - 1) * perPage + index + 1,
            width: "70px"
        },
        {
            name: <span className="fw-bold">Title</span>, selector: (row: ExpenseData) => row.title, sortable: true,
            sortField: "title", wrap: true
        },
        {
            name: <span className="fw-bold">Date</span>, selector: (row: ExpenseData) => displayDate(row.date), sortable: true,
            sortField: "date"
        },
        {
            name: <span className="fw-bold">Amount</span>, selector: (row: ExpenseData) => `${config.currency} ${row.amount}`,
            sortable: true, sortField: "amount"
        },
        {
            name: <span className="fw-bold">Actions</span>,
            width: "120px",
            cell: (row: ExpenseData) => (
                <div className="d-flex gap-2">
                    {canEdit && (
                        <Button size="sm" color="primary" onClick={() => handleEdit(row)} title="Edit" className="p-1 px-2">
                            <Edit size={14} />
                        </Button>
                    )}
                    {canDelete && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete" className="p-1 px-2">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };


    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Expense" parent="Dashboard" title="Add Expense" />
            <Container fluid>
                <Row>
                    {showForm && (
                        <Col md={4}>
                            <Card className="shadow-sm border-0">
                                <CardBody>
                                    <H3 className="mb-4">{isEditing ? 'Edit Expense' : 'Add Expense'}</H3>
                                    <Form onSubmit={handleSubmit(onSubmit)}>
                                        <FormGroup>
                                            <Label className="fw-bold small text-muted">Title
                                                <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="title"
                                                control={control}
                                                rules={{ required: "Title is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            {...field}
                                                            type="text"
                                                            placeholder="Expense Title"
                                                            invalid={!!errors.title}
                                                        />
                                                        <FormFeedback>{errors.title?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label className="fw-bold small text-muted">Description</Label>
                                            <Controller
                                                name="description"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        type="textarea"
                                                        rows="3"
                                                        placeholder="Description"
                                                    />
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label className="fw-bold small text-muted">Amount({config.currency}) <span
                                                className="text-danger">*</span></Label>
                                            <Controller
                                                name="amount"
                                                control={control}
                                                rules={{
                                                    required: "Amount is required",
                                                    min: { value: 0.01, message: "Amount must be greater than 0" }
                                                }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            {...field}
                                                            type="number"
                                                            placeholder="Amount"
                                                            invalid={!!errors.amount}
                                                        />
                                                        <FormFeedback>{errors.amount?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label className="fw-bold small text-muted">Date <span className="text-danger">*
                                            </span></Label>
                                            <Controller
                                                name="date"
                                                control={control}
                                                rules={{ required: "Date is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <DatePicker
                                                            wrapperClassName="w-100"
                                                            className={`form-control ${errors.date ? "is-invalid" : ""}`}
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date: Date) => field.onChange(date ? formatDate(date) :
                                                                "")}
                                                            placeholderText="Select Date"
                                                            dateFormat="dd/MM/yy"
                                                            peekNextMonth
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            dropdownMode="select"
                                                            autoComplete="off"
                                                        />
                                                        {errors.date && <div className="text-danger small mt-1">
                                                            {errors.date.message}</div>}
                                                    </>
                                                )}
                                            />
                                        </FormGroup>
                                        <div className="mt-4">
                                            <Button color="primary" block disabled={submitting} type="submit">
                                                {submitting ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        {isEditing ? "Updating..." : "Submitting..."}
                                                    </>
                                                ) : (
                                                    isEditing ? "Update" : "Submit"
                                                )}
                                            </Button>
                                            {isEditing && (
                                                <Button color="light" block onClick={() => { setIsEditing(false); setEditId(null); }} className="mt-2">
                                                    Cancel
                                                </Button>
                                            )}
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    )}

                    {/* Right Column: Expense Table */}
                    <Col md={showForm ? 8 : 12}>
                        <Card className="shadow-sm border-0">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div />
                                    <div style={{ width: '250px' }}>
                                        <InputGroup className="shadow-sm">
                                            <Input
                                                placeholder="Search..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="form-control-sm"
                                            />
                                        </InputGroup>
                                    </div>
                                </div>

                                <DataTable
                                    columns={columns}
                                    data={data}
                                    progressPending={loading}
                                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh', background: 'transparent' }}><Spinner color="primary" /></div>}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    onChangeRowsPerPage={(newSize, page) => { setPerPage(newSize); setCurrentPage(page); }}
                                    onChangePage={(page) => setCurrentPage(page)}
                                    sortServer
                                    onSort={(column, direction) => {
                                        setSortBy(column.sortField || 'id');
                                        setSortDir(direction);
                                    }}
                                    highlightOnHover
                                    customStyles={customStyles}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />
        </div>
    );
}
